import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { BookingError } from '@/components/booking/BookingError';
import { BookingSuccess } from '@/components/booking/BookingSuccess';
import { ConfirmationModal } from '@/components/booking/ConfirmationModal';
import { RescheduleBanner } from '@/components/booking/RescheduleBanner';
import { SlotList } from '@/components/booking/SlotList';
import { TimezoneSelector } from '@/components/booking/TimezoneSelector';
import { createBooking, rescheduleBooking, validateToken } from '@/services/bookingService';

import type { BookingConfirmation, BookingTokenData } from '@/types/scheduling.types';

type PageState = 'loading' | 'selecting' | 'success' | 'error';
type ErrorType = 'expired' | 'invalid' | 'generic';

export function BookingPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [tokenData, setTokenData] = useState<BookingTokenData | null>(null);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<BookingConfirmation | null>(null);
  const [errorType, setErrorType] = useState<ErrorType>('generic');
  const [timezone, setTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [reschedulingBookingId, setReschedulingBookingId] = useState<string | null>(null);
  const [previousConfirmation, setPreviousConfirmation] = useState<BookingConfirmation | null>(null);

  useEffect(() => {
    if (!token) {
      setErrorType('invalid');
      setPageState('error');
      return;
    }

    const load = async () => {
      try {
        const data = await validateToken(token);
        setTokenData(data);
        setPageState('selecting');
      } catch (err) {
        const message = err instanceof Error ? err.message : '';
        if (message.includes('expired')) {
          setErrorType('expired');
        } else if (message.includes('invalid') || message.includes('Invalid')) {
          setErrorType('invalid');
        } else {
          setErrorType('generic');
        }
        setPageState('error');
      }
    };

    void load();
  }, [token]);

  const selectedSlot = tokenData?.slots.find((s) => s.slotId === selectedSlotId) ?? null;

  const handleSelectSlot = useCallback((slotId: string) => {
    setSelectedSlotId((prev) => (prev === slotId ? null : slotId));
  }, []);

  const handleOpenConfirm = useCallback(() => {
    if (selectedSlotId) {
      setIsConfirmModalOpen(true);
    }
  }, [selectedSlotId]);

  const handleConfirm = useCallback(
    async (notes?: string) => {
      if (!token || !selectedSlotId) return;

      try {
        let result: BookingConfirmation;
        if (reschedulingBookingId) {
          result = await rescheduleBooking(reschedulingBookingId, token, selectedSlotId, notes);
        } else {
          result = await createBooking(token, selectedSlotId);
        }
        setConfirmation(result);
        setReschedulingBookingId(null);
        setPreviousConfirmation(null);
        setIsConfirmModalOpen(false);
        setPageState('success');
      } catch {
        toast.error(t('booking.error.generic'));
        setIsConfirmModalOpen(false);
      }
    },
    [token, selectedSlotId, reschedulingBookingId, t],
  );

  const handleReschedule = useCallback(() => {
    setPreviousConfirmation(confirmation);
    setReschedulingBookingId(confirmation?.bookingId ?? null);
    setConfirmation(null);
    setSelectedSlotId(null);
    setPageState('selecting');
  }, [confirmation]);

  const handleCancelReschedule = useCallback(() => {
    setReschedulingBookingId(null);
    setConfirmation(previousConfirmation);
    setPreviousConfirmation(null);
    setPageState('success');
  }, [previousConfirmation]);

  return (
    <div className='mx-auto max-w-2xl px-4 py-8'>
      {pageState === 'loading' && (
        <div className='flex flex-col items-center justify-center py-24'>
          <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent' />
          <p className='mt-4 text-sm text-gray-500'>{t('booking.loading')}</p>
        </div>
      )}

      {pageState === 'error' && <BookingError type={errorType} />}

      {pageState === 'selecting' && tokenData && (
        <>
          <div className='mb-6'>
            <h1 className='text-2xl font-bold text-gray-900'>{t('booking.title')}</h1>
            <p className='mt-1 text-sm text-gray-500'>{t('booking.subtitle')}</p>
            {tokenData.jobTitle && <p className='mt-2 text-sm font-medium text-gray-700'>{tokenData.jobTitle}</p>}
          </div>

          {reschedulingBookingId && previousConfirmation && (
            <RescheduleBanner
              currentStartUtc={previousConfirmation.startAtUtc}
              timezone={timezone}
              cutoffHours={24}
              onCancel={handleCancelReschedule}
            />
          )}

          <div className='mb-4'>
            <TimezoneSelector timezone={timezone} onChange={setTimezone} />
          </div>

          <SlotList
            slots={tokenData.slots}
            timezone={timezone}
            selectedSlotId={selectedSlotId}
            onSelectSlot={handleSelectSlot}
          />

          {selectedSlotId && (
            <div className='fixed bottom-0 left-0 right-0 border-t border-gray-200 bg-white p-4 shadow-lg'>
              <div className='mx-auto max-w-2xl'>
                <button
                  onClick={handleOpenConfirm}
                  className='w-full rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white hover:bg-blue-700'>
                  {t('booking.confirmBooking')}
                </button>
              </div>
            </div>
          )}

          <ConfirmationModal
            isOpen={isConfirmModalOpen}
            slot={selectedSlot}
            timezone={timezone}
            isRescheduling={!!reschedulingBookingId}
            onClose={() => setIsConfirmModalOpen(false)}
            onConfirm={handleConfirm}
          />

          {/* Spacer for sticky bottom bar */}
          {selectedSlotId && <div className='h-20' />}
        </>
      )}

      {pageState === 'success' && confirmation && (
        <BookingSuccess
          confirmation={confirmation}
          timezone={timezone}
          token={token ?? ''}
          onReschedule={handleReschedule}
        />
      )}
    </div>
  );
}
